import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { RiAspectRatioLine, RiCloseLine } from 'react-icons/ri';

import { useStore } from '../../hooks';
import TopbarIconButton from './TopbarIconButton';

const MENU_GAP = 8;
const VIEWPORT_PAD = 12;

const PRESET_GROUP_ORDER = [
  'bannerFrameGroupInstagram',
  'bannerFrameGroupFacebook',
  'bannerFrameGroupWhatsapp',
  'bannerFrameGroupVideo',
  'bannerFrameGroupLinkedin',
  'bannerFrameGroupTwitter',
  'bannerFrameGroupPinterest',
];

function FrameMenuItem({ label, hint, title, isActive, onClick }) {
  return (
    <button
      type="button"
      className={`respark_topbar-frame-menu-item respark_topbar-frame-menu-item--chip${isActive ? ' respark_topbar-frame-menu-item--active' : ''}`}
      title={title || label}
      onClick={onClick}
    >
      <span className="respark_topbar-frame-menu-item-body">
        <span className="respark_topbar-frame-menu-item-label">{label}</span>
        {hint ? <span className="respark_topbar-frame-menu-item-hint">{hint}</span> : null}
      </span>
    </button>
  );
}

function computeMenuPosition(anchorEl, panelEl) {
  if (!anchorEl) {
    return null;
  }

  const rect = anchorEl.getBoundingClientRect();
  const panelHeight = panelEl?.offsetHeight || 360;
  const panelWidth = panelEl?.offsetWidth || 0;
  const maxWidth = window.innerWidth - VIEWPORT_PAD * 2;
  let top = rect.bottom + MENU_GAP;

  let left = rect.left + rect.width / 2 - panelWidth / 2;
  if (panelWidth > 0) {
    left = Math.max(
      VIEWPORT_PAD,
      Math.min(left, window.innerWidth - panelWidth - VIEWPORT_PAD),
    );
  }

  if (top + panelHeight > window.innerHeight - VIEWPORT_PAD) {
    top = Math.max(VIEWPORT_PAD, rect.top - MENU_GAP - panelHeight);
  }

  return {
    top,
    left,
    maxWidth,
  };
}

const BannerFrameSizeToggle = () => {
  const { t, config: { bannerFrameControls } = {} } = useStore();
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState(null);
  const [customWidth, setCustomWidth] = useState('');
  const [customHeight, setCustomHeight] = useState('');
  const wrapRef = useRef(null);
  const panelRef = useRef(null);

  const closeMenu = useCallback(() => setOpen(false), []);

  const updatePanelPosition = useCallback(() => {
    if (!wrapRef.current) {
      return;
    }
    setPanelStyle(computeMenuPosition(wrapRef.current, panelRef.current));
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      return undefined;
    }
    updatePanelPosition();
    const raf = requestAnimationFrame(updatePanelPosition);
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [open, updatePanelPosition, customWidth, customHeight]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onPointerDown = (event) => {
      const target = event.target;
      if (
        wrapRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }
      closeMenu();
    };

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [closeMenu, open]);

  useEffect(() => {
    if (!bannerFrameControls?.width || !bannerFrameControls?.height) {
      return;
    }
    setCustomWidth(String(Math.round(bannerFrameControls.width)));
    setCustomHeight(String(Math.round(bannerFrameControls.height)));
  }, [bannerFrameControls?.width, bannerFrameControls?.height, open]);

  const groupedPresets = useMemo(() => {
    const presets = bannerFrameControls?.presets || [];
    const groups = presets.reduce((acc, preset) => {
      const key = preset.groupKey || 'bannerFrameGroupOther';
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(preset);
      return acc;
    }, {});

    return PRESET_GROUP_ORDER.filter((key) => groups[key]?.length).map(
      (key) => [key, groups[key]],
    );
  }, [bannerFrameControls?.presets]);

  if (!bannerFrameControls?.enabled) {
    return null;
  }

  const {
    width,
    height,
    framePreset = 'instagram_post',
    minDimension = 100,
    maxDimension = 4096,
    onSelectPreset,
    onApplyCustomSize,
  } = bannerFrameControls;

  const isCustomActive = framePreset === 'custom';

  const sizeLabel =
    width && height
      ? `${Math.round(width)} × ${Math.round(height)}`
      : t('bannerFrameSizeTitle');

  const handleSelect = (presetId) => {
    onSelectPreset?.(presetId);
    closeMenu();
  };

  const handleApplyCustom = () => {
    const w = Number.parseInt(customWidth, 10);
    const h = Number.parseInt(customHeight, 10);
    if (!Number.isFinite(w) || !Number.isFinite(h)) {
      return;
    }
    onApplyCustomSize?.(w, h);
    closeMenu();
  };

  const handleCustomKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleApplyCustom();
    }
  };

  const panel =
    open &&
    panelStyle &&
    createPortal(
      <div
        ref={panelRef}
        className="respark_topbar-frame-menu respark_topbar-frame-menu--rows"
        role="dialog"
        aria-modal="false"
        aria-label={t('bannerFrameSizeTitle')}
        style={panelStyle}
      >
        <div className="respark_topbar-frame-menu-header">
          <h2 className="respark_topbar-frame-menu-title">{t('bannerFrameSizeTitle')}</h2>
          <button
            type="button"
            className="respark_topbar-frame-menu-close"
            title="Close"
            aria-label="Close"
            onClick={closeMenu}
          >
            <RiCloseLine size={18} aria-hidden />
          </button>
        </div>
        <div className="respark_topbar-frame-menu-body">
          <div className="respark_topbar-frame-preset-rows" role="menu">
            <div
              className={`respark_topbar-frame-preset-row respark_topbar-frame-preset-row--custom${isCustomActive ? ' respark_topbar-frame-preset-row--active' : ''}`}
            >
              <div className="respark_topbar-frame-preset-row-label">
                {t('bannerFrameCustomSize')}
              </div>
              <div className="respark_topbar-frame-preset-row-items">
                <label className="respark_topbar-frame-custom-field">
                  <span className="respark_topbar-frame-custom-field-label">
                    {t('width')}
                  </span>
                  <input
                    type="number"
                    className="respark_topbar-frame-custom-input"
                    min={minDimension}
                    max={maxDimension}
                    value={customWidth}
                    aria-label={t('width')}
                    onChange={(event) => setCustomWidth(event.target.value)}
                    onKeyDown={handleCustomKeyDown}
                  />
                </label>
                <label className="respark_topbar-frame-custom-field">
                  <span className="respark_topbar-frame-custom-field-label">
                    {t('height')}
                  </span>
                  <input
                    type="number"
                    className="respark_topbar-frame-custom-input"
                    min={minDimension}
                    max={maxDimension}
                    value={customHeight}
                    aria-label={t('height')}
                    onChange={(event) => setCustomHeight(event.target.value)}
                    onKeyDown={handleCustomKeyDown}
                  />
                </label>
                <button
                  type="button"
                  className="respark_topbar-frame-custom-apply"
                  onClick={handleApplyCustom}
                >
                  {t('bannerFrameApplyCustom')}
                </button>
              </div>
            </div>

            {groupedPresets.map(([groupKey, groupItems]) => (
              <div key={groupKey} className="respark_topbar-frame-preset-row">
                <div className="respark_topbar-frame-preset-row-label">
                  {t(groupKey)}
                </div>
                <div className="respark_topbar-frame-preset-row-items">
                  {groupItems.map((preset) => (
                    <FrameMenuItem
                      key={preset.id}
                      label={t(preset.shortLabelKey || preset.labelKey)}
                      title={t(preset.labelKey)}
                      hint={`${preset.width} × ${preset.height}`}
                      isActive={framePreset === preset.id}
                      onClick={() => handleSelect(preset.id)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>,
      document.body,
    );

  return (
    <div className="respark_topbar-group respark_topbar-group--frame">
      <div ref={wrapRef} className="respark_topbar-frame-wrap">
        <span className="respark_topbar-frame-size-label">{sizeLabel}</span>
        <TopbarIconButton
          className={open ? 'respark_topbar-btn--on' : ''}
          title={t('bannerFrameSizeTitle')}
          ariaLabel={`${t('bannerFrameSizeTitle')}, ${sizeLabel}`}
          ariaPressed={open}
          onClick={() => setOpen((current) => !current)}
        >
          <RiAspectRatioLine size={18} aria-hidden />
        </TopbarIconButton>
        {panel}
      </div>
    </div>
  );
};

export default BannerFrameSizeToggle;
